import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useDeviceStatus } from '@/hooks/useDeviceStatus';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardBody, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { DeviceHero } from '@/components/devices/DeviceHero';
import { LicenseStrip } from '@/components/devices/LicenseStrip';
import { DeviceQuickStats } from '@/components/devices/DeviceQuickStats';
import { ModePanel } from '@/components/devices/ModePanel';
import { RelayGrid } from '@/components/devices/RelayGrid';
import { FlowGauge } from '@/components/devices/FlowGauge';
import { SettingsDrawer } from '@/components/devices/settings/SettingsDrawer';
import { expiryStatus } from '@/lib/license';

export function DeviceDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const { user, isSuperadmin, isViewer } = useAuth();
  const { data, isLoading, error } = useDeviceStatus(clientId);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (!clientId) return <Navigate to="/devices" replace />;
  if (!user) return null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-12" />
        <div className="grid md:grid-cols-2 gap-4">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardBody>
          <CardTitle>Device unreachable</CardTitle>
          <p className="text-sm text-slate-500 mt-1">We couldn't load this device. It may have been removed, or you may not have access.</p>
        </CardBody>
      </Card>
    );
  }

  // Inject the URL clientId so child components always have it (the status
  // payload doesn't include client_id explicitly).
  const device = { ...data, client_id: clientId };
  const lic = expiryStatus(device.expires_at);
  const expired = !isSuperadmin && lic.kind === 'expired';
  const gateDisabled = isViewer || expired;

  return (
    <div className="space-y-5">
      <DeviceHero
        device={device}
        canOpenSettings
        onOpenSettings={() => setDrawerOpen(true)}
      />

      <LicenseStrip
        deviceId={device.id}
        clientId={clientId}
        expires_at={device.expires_at}
        isSuperadmin={isSuperadmin}
      />

      <div className="grid lg:grid-cols-[360px_1fr] gap-5 items-start">
        <div className="space-y-5">
          <DeviceQuickStats device={device} />
          <ModePanel device={device} gateDisabled={gateDisabled} />
        </div>
        <div className="space-y-5">
          <FlowGauge flowRate={device.flowRate} totalLitres={device.totalLitres} />
          <Card>
            <CardBody>
              <div className="flex items-center justify-between mb-3">
                <CardTitle>Relays</CardTitle>
                <span className="text-xs text-slate-500">
                  {device.totalRelays ?? device.relays?.length ?? 0} channels
                </span>
              </div>
              <RelayGrid device={device} gateDisabled={gateDisabled} />
            </CardBody>
          </Card>
        </div>
      </div>

      <SettingsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        device={device}
        gateDisabled={gateDisabled}
        isSuperadmin={isSuperadmin}
      />
    </div>
  );
}
